import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, APP_INITIALIZER, ErrorHandler, LOCALE_ID } from '@angular/core';
import { ChunkErrorHandler, recoverFromChunkError } from './core/chunk-error';
import { provideRouter, withComponentInputBinding, withNavigationErrorHandler, TitleStrategy } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { firstValueFrom } from 'rxjs';
import { AppTitleStrategy } from './core/branding/app-title.strategy';

// 0.62.0.4: oidcBearerInterceptor aktiviert. Hintergrund: 0.62.0.2 hatte
// den fakeAuthInterceptor entfernt (Persona-Switcher-Removal Step 2/3) +
// dokumentiert dass der OIDC-Bearer-Interceptor stattdessen via
// FatipOidcAuthService.configure() registriert würde — der Code dazu wurde
// aber nie geschrieben. Bug-Wirkung: alle /api/-Calls gingen seit 0.62.0.2
// anonym ans Backend, User verloren ihre PLATFORM_ADMIN-Rechte. Fix
// (PO-Befund 2026-05-05): expliziter HttpInterceptorFn der den
// access_token aus angular-oauth2-oidc nimmt + an gleicher-Origin-API-
// Calls anhängt.
import { APP_ROUTES } from './app.routes';
import { oidcBearerInterceptor } from './core/auth/oidc-bearer.interceptor';
import { TemplatesService } from './core/api/templates.service';
import { BrandingService } from './core/branding/branding.service';
// Cut U23 (Issue #398): der Locale-Initializer und die zwei Stores dahinter.
import { FatipOidcAuthService } from './core/auth/fatip-oidc.service';
import { LocalePreferenceService } from './core/preferences/locale-preference.service';

// Cut U18 (issue #392): the four Angular locale data sets, registered at
// module scope. Without them Angular throws "Missing locale data" for any
// locale outside en-US, so formatDate and any locale-dependent rendering
// cannot run in de, fr, or it. en is the built-in default and is registered
// too, because the cut's scope names four.
registerLocaleData(localeDe);
registerLocaleData(localeEn);
registerLocaleData(localeFr);
registerLocaleData(localeIt);

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * Task 6 — UI-Overlay APP_INITIALIZER.
 *
 * After the static i18n JSON has been loaded by ngx-translate, this
 * initializer fetches the `ui` template overlay from
 *   GET /v1/content/ui?locale=<lang>
 * and merges it into the active translation via
 *   TranslateService.setTranslation(lang, overlayMap, shouldMerge=true)
 *
 * Key-mapping convention:
 *   DB template keys for type=ui ARE full ngx-translate keys
 *   (e.g. "i18n.fap.developer.signup_intro"). They map 1-to-1 —
 *   no conversion needed. Platform admins author keys using the
 *   exact translate key they want to override.
 *
 * On any fetch error the function silently ignores it so static i18n
 * continues to work without a backend connection.
 */
export function uiOverlayInitializerFactory(
  translate: TranslateService,
  templatesSvc: TemplatesService,
): () => Promise<void> {
  return async () => {
    // Wait for static translations to load (LangChangeEvent fires after first use).
    // We simply wait for the default language to be loaded via the loader.
    try {
      await firstValueFrom(translate.use(translate.defaultLang));
    } catch { /* ignore */ }

    const lang = translate.currentLang || translate.defaultLang || 'de';
    try {
      const overlayMap = await firstValueFrom(templatesSvc.uiOverlay(lang));
      if (overlayMap && typeof overlayMap === 'object' && Object.keys(overlayMap).length > 0) {
        translate.setTranslation(lang, overlayMap, /* shouldMerge */ true);
      }
    } catch {
      // Silently skip — static i18n stands, no degradation.
    }

    // Re-fetch the DB overlay whenever the user switches language so that
    // per-language configurable text is applied after every lang change.
    // APP_INITIALIZER lives for the app lifetime → bare subscribe is safe here.
    translate.onLangChange.subscribe(async ({ lang: newLang }) => {
      try {
        const overlay = await firstValueFrom(templatesSvc.uiOverlay(newLang));
        if (overlay && Object.keys(overlay).length) {
          translate.setTranslation(newLang, overlay, /* shouldMerge */ true);
        }
      } catch { /* silent — static i18n stands */ }
    });
  };
}

/**
 * Task 3 — Self-branding APP_INITIALIZER.
 *
 * Calls GET /v1/content/branding (PUBLIC, no auth required) at boot and
 * seeds BrandingService signals (appName, accentColor, backgroundColor,
 * logoUrl, faviconUrl, …) so the portal renders configured branding on
 * the very first paint.
 *
 * Resilience: mirrors uiOverlayInitializerFactory — any fetch failure
 * logs a console warning and leaves the static environment.branding
 * defaults in place. Boot is never blocked.
 */
export function brandingInitializerFactory(
  brandingSvc: BrandingService,
): () => Promise<void> {
  return async () => {
    try {
      await brandingSvc.loadFromBackend();
    } catch (err) {
      console.warn('[BrandingInit] Failed to load branding from backend; using static defaults.', err);
    }
  };
}

/**
 * Task 4 (Cut U23, Issue #398): Locale APP_INITIALIZER.
 *
 * Wendet die gespeicherte Sprache an, bevor der erste Render passiert:
 * localStorage synchron (Leg 1), die serverseitige Praeferenz sobald die
 * Session steht (Leg 2, Continuation auf oidc.bootstrapDone, nicht awaitet).
 * Der Plan nennt das "The initializer decision".
 *
 * Die Provider-Reihenfolge ist hier entscheidend (load bearing): dieser
 * Initializer muss NACH dem uiOverlayInitializerFactory-Eintrag stehen, weil
 * der translate.use(translate.defaultLang) awaitet und einen frueher
 * angewendeten Locale reverten wuerde. Angular awaitet APP_INITIALIZER in
 * Provider-Reihenfolge.
 *
 * Resilience: alle Server- und OIDC-Fehler sind still, die Continuation
 * faengt alles. Ein fehlender Katalog in Leg 1 bricht den Boot nicht ab,
 * die Fallback-Kette (localStorage, dann de) bleibt stehen. Der Seam ist
 * bewusst schmal (Pick): der Initializer braucht von TranslateService nur
 * currentLang + use und vom FatipOidcAuthService nur bootstrapDone +
 * authenticated, damit der Spec beides ohne echte Abhaengigkeiten faken kann.
 */
export function localeInitializerFactory(
  translate: Pick<TranslateService, 'currentLang' | 'use'>,
  localePrefs: LocalePreferenceService,
  oidc: Pick<FatipOidcAuthService, 'bootstrapDone' | 'authenticated'>,
): () => Promise<void> {
  return async () => {
    // Leg 1: localStorage synchron vor dem ersten Render. L01 (Reload,
    // Bookmark, eingefuegte URL) rendert in der gespeicherten Sprache ohne
    // Netz-Wartung; der Unterschied zum uiOverlay-Initializer oben ist die
    // Reihenfolge, nicht der Mechanismus.
    const stored = localePrefs.applyStored();
    if (stored && stored !== translate.currentLang) {
      try {
        await firstValueFrom(translate.use(stored));
      } catch {
        // Katalog fehlt oder Loader-Fehler: Boot nicht abbrechen, 'de' bleibt.
      }
    }

    // Leg 2: Server-Praeferenz, sobald die Session steht. NICHT awaitet,
    // damit der Boot nicht auf die IdP-Discovery wartet; der welcomeGateGuard
    // wartet auf dieselbe Promise fuer jede gated Route. Der Cross-Device-
    // Fall (kein localStorage-Eintrag, angemeldete Session) bleibt: 'de'
    // rendert zuerst, der Server-Wert ersetzt ihn (Plan, "Risks").
    void oidc.bootstrapDone
      .then(async () => {
        if (!oidc.authenticated()) {
          return;
        }
        const serverLocale = await localePrefs.loadServerLocale();
        if (serverLocale && serverLocale !== translate.currentLang) {
          await firstValueFrom(translate.use(serverLocale));
          // persist(lang, true) schreibt localStorage UND den Server-Write
          // (Write-both-Regel des Service); der PUT ist fire-and-forget.
          localePrefs.persist(serverLocale, true);
        }
      })
      .catch(() => undefined);
  };
}

/**
 * Task 2 (Cut U18, issue #392): the LOCALE_ID source at boot.
 *
 * Reads the same validated localStorage entry (de|en|fr|it, unknown reads
 * as null) the locale initializer's Leg 1 above applies synchronously before
 * the first render, so the provider and the first render agree at boot.
 * Falls back to 'de', the same chain the initializer keeps standing.
 *
 * Divergence edge: when the Leg 1 translate.use() fails (a missing catalog
 * or a loader error), Leg 1 keeps 'de' for the UI strings while this
 * provider still resolves the stored locale, so the two can diverge in
 * that edge. Immaterial today: every date consumer of this provider
 * renders a locale-fixed pattern, so the divergence changes nothing for
 * them (the direct TS-side consumer, org-ingest-tab, formats dates, and
 * no locale-dependent format exists yet).
 *
 * Boot-time limit (plan U18, "The boot-time limit"): LOCALE_ID is read once
 * per pipe instance and once at bootstrap. A language switch after boot does
 * not re-resolve it for pipe instances already created. The shared date
 * pattern is fixed, so the date rendering does not depend on the locale; a
 * cut that adds a locale-dependent format later re-examines that paragraph
 * before relying on LOCALE_ID.
 */
export function fapLocaleIdFactory(localePrefs: LocalePreferenceService): string {
  return localePrefs.applyStored() ?? 'de';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // withNavigationErrorHandler faengt ChunkLoadError beim Lazy-Route-Laden
    // (z.B. Platform Management) nach einem Deploy ab -> Ein-Schuss-Reload.
    provideRouter(APP_ROUTES, withComponentInputBinding(),
      withNavigationErrorHandler((err) => { recoverFromChunkError(err); })),
    // Globaler Handler fuer ChunkLoadError aus dynamischen Imports ausserhalb des Routers.
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
    provideAnimations(),
    provideHttpClient(withInterceptors([oidcBearerInterceptor])),
    // Pfad C.4 (ADR-072/073) — IdP-OIDC-Login via angular-oauth2-oidc.
    // Konfiguration wird zur Laufzeit aus environment.ts gezogen
    // (FatipOidcAuthService konfiguriert OAuthService.configure()).
    // Authorization-Code-Flow + PKCE; Refresh-Tokens via offline_access-Scope.
    provideOAuthClient({
      resourceServer: {
        // Bei expliziten URLs werden Bearer-Tokens nur dorthin geschickt.
        // Wir konfigurieren das im FatipOidcAuthService dynamisch.
        sendAccessToken: false,
      },
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'de',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        }
      })
    ),
    // Task 6 — UI-Overlay: merges DB ui-template content into ngx-translate
    // after the static i18n JSON loads. On error → silent fallback to static i18n.
    {
      provide: APP_INITIALIZER,
      useFactory: uiOverlayInitializerFactory,
      deps: [TranslateService, TemplatesService],
      multi: true,
    },
    // Task 4 (Cut U23, Issue #398): Locale. Applies the localStorage locale
    // synchronously and the server locale once the session is established.
    // Order is load bearing: this entry MUST stay AFTER the ui-overlay entry
    // above, because that one awaits translate.use(translate.defaultLang)
    // and would revert a locale applied earlier. Angular awaits APP_INITIALIZER
    // in provider order. On error → silent, boot never fails.
    {
      provide: APP_INITIALIZER,
      useFactory: localeInitializerFactory,
      deps: [TranslateService, LocalePreferenceService, FatipOidcAuthService],
      multi: true,
    },
    // Task 3 — Self-branding: loads GET /v1/content/branding at boot to seed
    // BrandingService signals. On error → silent fallback to environment.branding
    // defaults. Never blocks boot.
    {
      provide: APP_INITIALIZER,
      useFactory: brandingInitializerFactory,
      deps: [BrandingService],
      multi: true,
    },
    // Task 2 (Cut U18, issue #392): LOCALE_ID from the active language. The
    // four locale data sets are registered at module scope above; the factory
    // reads the same stored locale the initializer's Leg 1 applies. The two
    // agree at boot except in the Leg 1 loader-failure edge (the catch above
    // keeps 'de' while this provider still resolves the stored locale) — see
    // the divergence note on fapLocaleIdFactory above.
    { provide: LOCALE_ID, useFactory: fapLocaleIdFactory, deps: [LocalePreferenceService] },
    // Bug #8 — Custom TitleStrategy: replaces the hardcoded "FATIP" brand
    // prefix in route titles with the configured app name from BrandingService,
    // so the browser tab reflects whatever appName the tenant has set.
    { provide: TitleStrategy, useClass: AppTitleStrategy },
  ]
};
